import React, { useEffect, useMemo, useRef, useState } from "react";
import AcceptedCardBrands from "../payments/AcceptedCardBrands";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Lock,
  ShieldCheck,
} from "lucide-react";
import { bookingPaymentCallable } from "../../utils/bookingPaymentCallable";
import { decodeThreeDsHtml, WompiThreeDsFrame } from "../payments/WompiThreeDsFrame";
import {
  cleanInlinePaymentValue as clean,
  inlineDigitsOnly as digitsOnly,
  formatInlineCardNumber as formatCardNumber,
  inlineCardBrand as cardBrandFromNumber,
  inlineCardFieldErrors as paymentFieldErrors,
  normalizeInlineCard as normalizeCard,
  isValidInlineWompiSetup,
  inlineProviderState,
} from "../../utils/publicBookingWompiInlineClient";


function emptyCard() {
  return {
    cardHolder: "",
    number: "",
    expMonth: "",
    expYear: "",
    cvc: "",
  };
}

function moneyFromCents(value) {
  const cents = Number(value || 0);
  try {
    return new Intl.NumberFormat("es-PA", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number.isFinite(cents) ? cents / 100 : 0);
  } catch (_) {
    return `$${((Number.isFinite(cents) ? cents : 0) / 100).toFixed(2)}`;
  }
}

function base64Url(bytes) {
  let binary = "";
  const input = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < input.length; i += 1) {
    binary += String.fromCharCode(input[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function pemToArrayBuffer(pem) {
  const normalized = clean(pem)
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");
  if (!normalized) throw new Error("Wompi no devolvió una llave de cifrado válida.");
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function encryptCardJwe(card, pem) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || !globalThis.crypto?.getRandomValues) {
    throw new Error("Este dispositivo no soporta el cifrado requerido para pagar.");
  }

  const rsaKey = await subtle.importKey(
    "spki",
    pemToArrayBuffer(pem),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );

  const cekBytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const cek = await subtle.importKey(
    "raw",
    cekBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const protectedPart = base64Url(
    new TextEncoder().encode(JSON.stringify({
      alg: "RSA-OAEP-256",
      enc: "A256GCM",
    }))
  );
  const encryptedKey = await subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaKey,
    cekBytes
  );
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(card));
  const encrypted = new Uint8Array(
    await subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: new TextEncoder().encode(protectedPart),
        tagLength: 128,
      },
      cek,
      plaintext
    )
  );

  if (encrypted.length <= 16) throw new Error("No pudimos cifrar la tarjeta.");
  const ciphertext = encrypted.slice(0, encrypted.length - 16);
  const tag = encrypted.slice(encrypted.length - 16);

  return [
    protectedPart,
    base64Url(encryptedKey),
    base64Url(iv),
    base64Url(ciphertext),
    base64Url(tag),
  ].join(".");
}

function loadWompiFingerprint(publicKey) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("Wompi fingerprint no disponible."));
      return;
    }

    const initialize = () => {
      const wompi = window.$wompi;
      if (!wompi || typeof wompi.initialize !== "function") {
        reject(new Error("Wompi no pudo iniciar la protección antifraude."));
        return;
      }
      wompi.initialize((data, error) => {
        if (error) {
          reject(new Error("Wompi no pudo validar este dispositivo."));
          return;
        }
        const sessionId = clean(data?.sessionId);
        const deviceId = clean(data?.deviceData?.deviceID);
        if (!sessionId) {
          reject(new Error("Wompi no devolvió la sesión antifraude."));
          return;
        }
        resolve({ sessionId, deviceId });
      });
    };

    if (window.$wompi && typeof window.$wompi.initialize === "function") {
      initialize();
      return;
    }

    const selector = 'script[data-loop-wompi-fingerprint="true"]';
    const scriptUrl = "https://wompijs.wompi.com/libs/js/v1.js";
    let existing = document.querySelector(selector);
    if (existing && existing.src !== scriptUrl) {
      existing.remove();
      existing = null;
    }
    if (existing) {
      existing.addEventListener("load", initialize, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("No se pudo cargar la protección de Wompi.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.setAttribute("data-public-key", publicKey);
    script.setAttribute("data-loop-wompi-fingerprint", "true");
    script.onload = initialize;
    script.onerror = () => {
      script.remove();
      reject(new Error("No se pudo cargar la protección de Wompi."));
    };
    document.head.appendChild(script);
  });
}

function paymentErrorMessage(error) {
  const raw = String(
    [
      error?.code,
      error?.message,
      error?.details?.reason,
      error || "",
    ].filter(Boolean).join(" ")
  ).toLowerCase();

  if (raw.includes("payment-hold-expired")) {
    return "El tiempo reservado para este horario venció. Selecciona el horario nuevamente.";
  }
  if (raw.includes("attempt-in-progress") || raw.includes("aborted")) {
    return "Ya estamos procesando este pago. Espera unos segundos.";
  }
  if (raw.includes("acceptance")) {
    return "Acepta los términos de Wompi para continuar.";
  }
  if (raw.includes("inline-wompi-production-not-enabled")) {
    return "El pago integrado todavía está habilitado solo para pruebas.";
  }
  return error?.message || "No pudimos completar el pago con Wompi.";
}

export default function PublicBookingWompiInlineCard({
  businessId,
  bookingId,
  session,
  balanceCents = 0,
  onApproved,
  onStatus,
  recoveryState = null,
}) {
  const [retrySession, setRetrySession] = useState(null);
  const reference = clean((retrySession || session)?.reference);
  const amountInCents = Number((retrySession || session)?.amountInCents || 0);
  const [setup, setSetup] = useState(null);
  const usesSingleSource = setup?.paymentFlow === "booking_single_source_v1";
  const [setupLoading, setSetupLoading] = useState(!recoveryState);
  const [fingerprint, setFingerprint] = useState(null);
  const [fingerprintLoading, setFingerprintLoading] = useState(false);
  const [fingerprintAttempt, setFingerprintAttempt] = useState(0);
  const [card, setCard] = useState(emptyCard);
  const [touched, setTouched] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [providerState, setProviderState] = useState(recoveryState);
  const [polling, setPolling] = useState(Boolean(recoveryState && inlineProviderState(recoveryState) === "pending"));
  const pollCountRef = useRef(0);
  const yearRef = useRef(null);
  const cvcRef = useRef(null);

  const fieldErrors = useMemo(() => paymentFieldErrors(card), [card]);
  const cardReady = Object.keys(fieldErrors).length === 0;
  const brand = useMemo(() => cardBrandFromNumber(card.number), [card.number]);
  const amountLabel = moneyFromCents(amountInCents);
  const balanceLabel = moneyFromCents(balanceCents);

  const emitStatus = (message) => {
    if (typeof onStatus === "function") onStatus(message);
  };

  const loadSetup = async () => {
    if (recoveryState) return;
    if (!businessId || !bookingId || !reference) return;
    setSetupLoading(true);
    setError("");
    try {
      const callable = bookingPaymentCallable(
        "getPublicBookingWompiInlineSetup"
      );
      const result = await callable({ businessId, bookingId, reference });
      const data = result?.data || {};
      if (!isValidInlineWompiSetup(data, amountInCents)) {
        throw new Error("El servidor no devolvió una configuración de pago válida.");
      }
      setSetup(data);
      emitStatus("Pago seguro listo. Completa los datos de tu tarjeta.");
    } catch (err) {
      console.error("[PublicBookingWompiInlineCard] setup failed", err);
      setError(paymentErrorMessage(err));
    } finally {
      setSetupLoading(false);
    }
  };

  const retryDeclinedPayment = async () => {
    if (recoveryState) return;
    setBusy(true);
    setError("");
    try {
      const retry = bookingPaymentCallable("retryPublicBookingWompiPayment");
      const result = await retry({ businessId, bookingId, reference });
      const next = result?.data?.session;
      if (!result?.data?.ok || !next?.reference || next.reference === reference ||
          next.amountInCents !== amountInCents || next.environment !== setup?.environment) {
        throw new Error("No pudimos preparar un nuevo intento seguro.");
      }
      setSetup(null);
      setProviderState(null);
      setFingerprint(null);
      setAccepted(false);
      setCard(emptyCard());
      setTouched({});
      setRetrySession(next);
    } catch (err) { setError(paymentErrorMessage(err)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!recoveryState) loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, bookingId, reference, amountInCents]);

  useEffect(() => {
    if (!setup?.merchantPublicKey || usesSingleSource) return undefined;
    let active = true;
    setError("");
    setFingerprintLoading(true);
    loadWompiFingerprint(setup.merchantPublicKey)
      .then((data) => {
        if (active) setFingerprint(data);
      })
      .catch((err) => {
        if (active) {
          console.error("[PublicBookingWompiInlineCard] fingerprint failed", err);
          setError(paymentErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) setFingerprintLoading(false);
      });
    return () => {
      active = false;
    };
  }, [setup?.merchantPublicKey, fingerprintAttempt, usesSingleSource]);

  const handleApproved = async (statusData) => {
    setPolling(false);
    setBusy(false);
    setProviderState(statusData);
    emitStatus("Pago aprobado. Confirmando tu reserva...");
    if (typeof onApproved === "function") {
      await onApproved(statusData);
    }
  };

  const applyProviderState = async (data) => {
    setProviderState(data);

    const state = inlineProviderState(data);

    if (state === "approved") {
      await handleApproved(data);
      return "approved";
    }

    if (state === "final") {
      setPolling(false);
      setBusy(false);
      setError(
        data?.statusMessage ||
        "Wompi no aprobó este intento. No se registró un abono."
      );
      emitStatus("El pago no fue aprobado.");
      return "final";
    }

    setPolling(true);
    emitStatus("Wompi está verificando el pago...");
    return "pending";
  };

  useEffect(() => {
    if (!polling || !businessId || !bookingId || !reference) return undefined;
    let cancelled = false;
    let inFlight = false;
    const pollOnce = async () => {
      if (cancelled || inFlight || document.visibilityState === "hidden") return;
      pollCountRef.current += 1;
      if (pollCountRef.current > 100) {
        window.clearInterval(timer);
        setPolling(false);
        setBusy(false);
        setError("Wompi sigue procesando el pago. Revisa Mis Reservas antes de intentarlo nuevamente.");
        return;
      }

      try {
        inFlight = true;
        const callable = bookingPaymentCallable(
          "getPublicBookingWompiInlineTransactionStatus"
        );
        const result = await callable({ businessId, bookingId, reference });
        if (!cancelled) {
          const outcome = await applyProviderState(result?.data || {});
          if (outcome !== "pending") window.clearInterval(timer);
        }
      } catch (err) {
        console.warn("[PublicBookingWompiInlineCard] status poll failed", err);
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(pollOnce, 1800);
    const resume = () => { if (document.visibilityState !== "hidden") void pollOnce(); };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, businessId, bookingId, reference]);

  const startPayment = async () => {
    if (recoveryState) return;
    setTouched({
      cardHolder: true,
      number: true,
      expMonth: true,
      expYear: true,
      cvc: true,
    });
    setError("");

    if (!setup || setupLoading) return;
    if (!cardReady) {
      setError(Object.values(fieldErrors)[0] || "Revisa los datos de la tarjeta.");
      return;
    }
    if (!accepted) {
      setError("Acepta los términos de Wompi para continuar.");
      return;
    }
    if (!usesSingleSource && !fingerprint?.sessionId) {
      setError("Espera un momento mientras Wompi valida este dispositivo.");
      return;
    }

    setBusy(true);
    emitStatus(`Procesando ${amountLabel} de forma segura...`);

    try {
      const normalized = normalizeCard(card);
      const encrypted = await encryptCardJwe(
        normalized,
        setup.tokenization.publicKey
      );

      const callable = bookingPaymentCallable(
        "createPublicBookingWompiInlineCardTransaction"
      );
      const result = await callable({
        businessId,
        bookingId,
        reference,
        payload: encrypted,
        accepted: true,
        sessionId: fingerprint?.sessionId || null,
        deviceId: fingerprint?.deviceId || null,
      });

      setCard(emptyCard());
      setTouched({});
      const outcome = await applyProviderState(result?.data || {});
      if (outcome === "pending") {
        pollCountRef.current = 0;
      }
    } catch (err) {
      console.error("[PublicBookingWompiInlineCard] payment failed", err);
      setBusy(false);
      setError(paymentErrorMessage(err));
      emitStatus("No pudimos completar el pago.");
    }
  };

  const threeDs = providerState?.threeDs || null;
  const paymentApproved = inlineProviderState(providerState) === "approved";
  const threeDsHtml = decodeThreeDsHtml(threeDs?.methodData || "");
  const threeDsStep = clean(threeDs?.currentStep).toUpperCase();
  const isChallenge = threeDsStep === "CHALLENGE";
  const shouldRunThreeDs = inlineProviderState(providerState) === "pending" &&
    ["BROWSER_INFO", "FINGERPRINT", "CHALLENGE"].includes(threeDsStep) &&
    clean(threeDs?.currentStepStatus).toUpperCase() !== "COMPLETED";

  return (
    <div className="loop-inline-pay-card">
      <style>{`
        .loop-inline-pay-card{border:1px solid #dbe7e5;border-radius:18px;background:#fff;padding:16px;box-shadow:0 10px 30px rgba(15,23,42,.06);display:grid;gap:14px}
        .loop-inline-pay-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
        .loop-inline-pay-kicker{display:flex;align-items:center;gap:6px;color:#047857;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}
        .loop-inline-pay-head strong{display:block;margin-top:4px;color:#0f172a;font-size:17px;font-weight:950;letter-spacing:-.02em}
        .loop-inline-pay-head small{display:block;margin-top:4px;color:#64748b;font-size:11.5px;font-weight:700;line-height:1.4}
        .loop-inline-pay-brands{display:flex;gap:5px;align-items:center}
        .loop-inline-pay-brand{border:1px solid #dce5e7;border-radius:7px;background:#fff;padding:4px 6px;color:#64748b;font-size:9px;font-weight:950}
        .loop-inline-pay-brand.is-active{border-color:#5eead4;color:#047857;background:#f0fdfa}
        .loop-inline-pay-form{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .loop-inline-pay-field{display:grid;gap:5px;min-width:0}
        .loop-inline-pay-field.wide{grid-column:1/-1}
        .loop-inline-pay-field span{color:#475569;font-size:10.5px;font-weight:850}
        .loop-inline-pay-field input{width:100%;box-sizing:border-box;border:1px solid #d9e2e7;border-radius:11px;background:#fff;padding:10px 11px;color:#0f172a;font-size:13px;font-weight:750;outline:none}
        .loop-inline-pay-field input:focus{border-color:#2dd4bf;box-shadow:0 0 0 3px rgba(45,212,191,.11)}
        .loop-inline-pay-field input[aria-invalid=true]{border-color:#fca5a5}
        .loop-inline-pay-error-text{color:#b91c1c;font-size:9.5px;font-weight:750}
        .loop-inline-pay-breakdown{display:grid;gap:7px;border:1px solid #ccfbf1;border-radius:13px;background:#f0fdfa;padding:11px 12px}
        .loop-inline-pay-breakdown>div{display:flex;justify-content:space-between;gap:12px;color:#475569;font-size:11.5px;font-weight:800}
        .loop-inline-pay-breakdown>div:first-child{color:#047857;font-weight:950}
        .loop-inline-pay-consent{display:flex;gap:9px;align-items:flex-start;cursor:pointer;color:#475569;font-size:10.5px;font-weight:700;line-height:1.4}
        .loop-inline-pay-consent input{margin-top:2px;accent-color:#059669}
        .loop-inline-pay-consent strong{display:block;color:#0f172a;font-size:11px;font-weight:900}
        .loop-inline-pay-consent a{display:inline-flex;align-items:center;gap:4px;margin-top:3px;color:#047857;font-weight:900;text-decoration:none}
        .loop-inline-pay-primary{width:100%;border:0;border-radius:12px;background:#059669;color:#fff;padding:12px 14px;font-size:13px;font-weight:950;cursor:pointer}
        .loop-inline-pay-primary:disabled{cursor:not-allowed;background:#94d5c5}
        .loop-inline-pay-foot{display:flex;align-items:center;gap:7px;color:#64748b;font-size:10px;font-weight:750;line-height:1.35}
        .loop-inline-pay-state,.loop-inline-pay-error,.loop-inline-pay-success{display:flex;align-items:flex-start;gap:9px;border-radius:12px;padding:11px 12px;font-size:11px;font-weight:800;line-height:1.4}
        .loop-inline-pay-state{background:#f8fafc;color:#475569}
        .loop-inline-pay-error{background:#fff1f2;color:#9f1239}
        .loop-inline-pay-success{background:#ecfdf5;color:#047857}
        .loop-inline-pay-state button,.loop-inline-pay-error button{margin-left:auto;border:0;background:transparent;color:inherit;font-weight:950;cursor:pointer}
        .loop-inline-pay-spin{animation:loop-inline-pay-spin .85s linear infinite;flex:0 0 auto}
        @keyframes loop-inline-pay-spin{to{transform:rotate(360deg)}}
        .loop-inline-pay-3ds{width:100%;min-height:430px;border:1px solid #dbe7e5;border-radius:12px;background:#fff}
        .loop-inline-pay-3ds-hidden{width:1px;height:1px;border:0;position:fixed;left:0;top:0;opacity:.01;pointer-events:none}
        @media(max-width:680px){.loop-inline-pay-card{padding:14px;border-radius:15px}.loop-inline-pay-form{grid-template-columns:1fr 1fr}.loop-inline-pay-head{align-items:center}.loop-inline-pay-head small{font-size:10.5px}}
      `}</style>

      <div className="loop-inline-pay-head">
        <div>
          <strong>Pagar con tarjeta</strong>
        </div>
        <AcceptedCardBrands selectedBrand={brand} />
      </div>

      {setupLoading ? (
        <div className="loop-inline-pay-state">
          <Loader2 className="loop-inline-pay-spin" size={16} />
          Preparando pago seguro...
        </div>
      ) : null}

      {setup?.environment === "sandbox" ? (
        <div className="loop-inline-pay-state" role="status">
          Modo de prueba. Usa solamente datos de tarjeta de prueba de Wompi.
        </div>
      ) : null}


      {!setupLoading && fingerprintLoading ? (
        <div className="loop-inline-pay-state">
          <Loader2 className="loop-inline-pay-spin" size={16} />
          Protegiendo este dispositivo con Wompi...
        </div>
      ) : null}

      {error ? (
        <div className="loop-inline-pay-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
          {!busy && !setup ? <button type="button" onClick={recoveryState ? () => { pollCountRef.current = 0; setError(""); setPolling(true); } : loadSetup}>{recoveryState ? "Consultar de nuevo" : "Reintentar"}</button> : null}
          {!usesSingleSource && !busy && setup && !fingerprint?.sessionId && !fingerprintLoading ? (
            <button type="button" onClick={() => setFingerprintAttempt((value) => value + 1)}>Reintentar protección</button>
          ) : null}
        </div>
      ) : null}

      {recoveryState && !paymentApproved ? <div role="status">{inlineProviderState(providerState) === "pending" ? "Consultando el pago existente. Completa la verificación del banco si aparece abajo." : "Este intento terminó sin aprobación. Solicita al negocio revisar el resultado antes de iniciar otro pago."}</div> : null}
      {!recoveryState && providerState?.status === "DECLINED" ? (
        <button type="button" disabled={busy || polling} onClick={retryDeclinedPayment}>
          {busy ? "Preparando nuevo intento..." : "Reintentar pago en esta reserva"}
        </button>
      ) : null}

      {paymentApproved ? (
        <div className="loop-inline-pay-success">
          <CheckCircle2 size={17} />
          Pago aprobado. Estamos terminando de confirmar tu reserva.
        </div>
      ) : null}

      {setup && !paymentApproved ? (
        <>
          <div className="loop-inline-pay-form">
            <label className="loop-inline-pay-field wide">
              <span>Nombre en la tarjeta</span>
              <input
                type="text"
                autoComplete="cc-name"
                value={card.cardHolder}
                placeholder="Como aparece en la tarjeta"
                aria-invalid={Boolean(touched.cardHolder && fieldErrors.cardHolder)}
                onBlur={() => setTouched((prev) => ({ ...prev, cardHolder: true }))}
                onChange={(event) => setCard((prev) => ({ ...prev, cardHolder: event.target.value }))}
                disabled={busy || polling}
              />
              {touched.cardHolder && fieldErrors.cardHolder ? <small className="loop-inline-pay-error-text">{fieldErrors.cardHolder}</small> : null}
            </label>

            <label className="loop-inline-pay-field wide">
              <span>Número de tarjeta</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                maxLength={23}
                value={card.number}
                placeholder="1234 5678 9012 3456"
                aria-invalid={Boolean(touched.number && fieldErrors.number)}
                onBlur={() => setTouched((prev) => ({ ...prev, number: true }))}
                onChange={(event) => setCard((prev) => ({ ...prev, number: formatCardNumber(event.target.value) }))}
                disabled={busy || polling}
              />
              {touched.number && fieldErrors.number ? <small className="loop-inline-pay-error-text">{fieldErrors.number}</small> : null}
            </label>

            <label className="loop-inline-pay-field">
              <span>Mes</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-month"
                maxLength={2}
                value={card.expMonth}
                placeholder="MM"
                aria-invalid={Boolean(touched.expMonth && fieldErrors.expMonth)}
                onBlur={() => {
                  const raw = digitsOnly(card.expMonth, 2);
                  setCard((prev) => ({ ...prev, expMonth: raw.length === 1 ? `0${raw}` : raw }));
                  setTouched((prev) => ({ ...prev, expMonth: true }));
                }}
                onChange={(event) => {
                  const next = digitsOnly(event.target.value, 2);
                  setCard((prev) => ({ ...prev, expMonth: next }));
                  if (next.length === 2) window.setTimeout(() => yearRef.current?.focus(), 0);
                }}
                disabled={busy || polling}
              />
              {touched.expMonth && fieldErrors.expMonth ? <small className="loop-inline-pay-error-text">{fieldErrors.expMonth}</small> : null}
            </label>

            <label className="loop-inline-pay-field">
              <span>Año</span>
              <input
                ref={yearRef}
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-year"
                maxLength={2}
                value={card.expYear}
                placeholder="AA"
                aria-invalid={Boolean(touched.expYear && fieldErrors.expYear)}
                onBlur={() => setTouched((prev) => ({ ...prev, expYear: true }))}
                onChange={(event) => {
                  const next = digitsOnly(event.target.value, 2);
                  setCard((prev) => ({ ...prev, expYear: next }));
                  if (next.length === 2) window.setTimeout(() => cvcRef.current?.focus(), 0);
                }}
                disabled={busy || polling}
              />
              {touched.expYear && fieldErrors.expYear ? <small className="loop-inline-pay-error-text">{fieldErrors.expYear}</small> : null}
            </label>

            <label className="loop-inline-pay-field wide">
              <span>CVC</span>
              <input
                ref={cvcRef}
                type="password"
                inputMode="numeric"
                autoComplete="cc-csc"
                maxLength={4}
                value={card.cvc}
                placeholder="123"
                aria-invalid={Boolean(touched.cvc && fieldErrors.cvc)}
                onBlur={() => setTouched((prev) => ({ ...prev, cvc: true }))}
                onChange={(event) => setCard((prev) => ({ ...prev, cvc: digitsOnly(event.target.value, 4) }))}
                disabled={busy || polling}
              />
              {touched.cvc && fieldErrors.cvc ? <small className="loop-inline-pay-error-text">{fieldErrors.cvc}</small> : null}
            </label>
          </div>

          <div className="loop-inline-pay-breakdown">
            <div><span>Pagar ahora</span><strong>{amountLabel}</strong></div>
            <div><span>Saldo después</span><strong>{balanceLabel}</strong></div>
          </div>

          <label className="loop-inline-pay-consent">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              disabled={busy || polling}
            />
            <span>
              <strong>Autorizar este pago</strong>
              Acepto los términos vigentes de Wompi para procesar este cobro.
              {setup?.acceptance?.permalink ? (
                <a href={setup.acceptance.permalink} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                  Ver términos <ExternalLink size={11} />
                </a>
              ) : null}
            </span>
          </label>

          <button
            type="button"
            className="loop-inline-pay-primary"
            onClick={startPayment}
            disabled={
              !cardReady ||
              !accepted ||
              inlineProviderState(providerState || {}) === "final" ||
              (!usesSingleSource && !fingerprint?.sessionId) ||
              fingerprintLoading ||
              busy ||
              polling
            }
          >
            {busy || polling
              ? "Procesando de forma segura..."
              : `Pagar ${amountLabel}`}
          </button>

          <div className="loop-inline-pay-foot">
            <Lock size={14} />
            <span>Pago seguro con Wompi.</span>
          </div>
        </>
      ) : null}

      {threeDsHtml && shouldRunThreeDs ? (
        isChallenge ? (
          <div className="loop-inline-pay-state" style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CreditCard size={16} />
              <strong>Tu banco necesita verificar la tarjeta</strong>
            </div>
            <WompiThreeDsFrame title="Verificación segura de Wompi" className="loop-inline-pay-3ds" html={threeDsHtml} />
          </div>
        ) : (
          <WompiThreeDsFrame title="Verificación segura de Wompi" className="loop-inline-pay-3ds-hidden" html={threeDsHtml} hiddenExecution />
        )
      ) : null}
    </div>
  );
}
